import {
  Component,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import { AppDialogContainerComponent } from './dialog-container.component';
import { DialogCloseDirective } from './dialog-close.directive';

let nextHeadingId: number = 0;

@Component({
  selector: 'app-dialog-header',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, TranslateModule, DialogCloseDirective],
  template: `
    <div class="flex flex-col gap-2 px-6 pt-6">
      <div class="flex items-center w-full gap-2">
        @if (backButton()) {
          <button
            mat-icon-button
            type="button"
            (click)="back.emit()"
            [attr.aria-label]="'Back' | translate"
            data-testid="dialog-back">
            <mat-icon class="!text-on-surface-variant">arrow_back</mat-icon>
          </button>
        } @else {
          <div class="size-10 shrink-0"></div>
        }

        <div class="flex-1 min-w-0">
          @if (title()) {
            <h2 #heading [id]="headingId" class="title-large text-on-surface m-0 truncate">
              {{ title() }}
            </h2>
          }
          @if (description()) {
            <p class="body-medium text-on-surface-variant m-0 mt-1">{{ description() }}</p>
          }
        </div>

        <div class="flex items-center gap-1 shrink-0">
          <ng-content select="[dialogHeaderActions]" />
          @if (closable()) {
            <button
              mat-icon-button
              type="button"
              appDialogClose
              class="w-auto"
              [attr.aria-label]="'Close' | translate"
              data-testid="dialog-close">
              <mat-icon class="!text-on-surface-variant">close</mat-icon>
            </button>
          } @else {
            <div class="size-10 shrink-0"></div>
          }
        </div>
      </div>
    </div>
  `,
})
export class DialogHeaderComponent {
  readonly title = input<string | undefined>();
  readonly description = input<string | undefined>();
  readonly closable = input(true);
  readonly backButton = input(false);

  readonly back = output<void>();

  readonly headingId: string;
  private readonly heading = viewChild<ElementRef<HTMLElement>>('heading');
  private readonly container = inject(AppDialogContainerComponent, { optional: true });

  constructor() {
    nextHeadingId += 1;
    this.headingId = `app-dialog-heading-${nextHeadingId}`;

    queueMicrotask(() => this.registerLabel());
  }

  private registerLabel(): void {
    if (this.title() && this.container) {
      this.container._addAriaLabelledBy(this.headingId);
    }
  }
}
